import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewGuest } from '../../hooks/use-get-default-values';
import {
  ActivityPurchaseFormType,
  ActivityPurchaseGuestsFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetActivityGuestTableList(): IColumn<ActivityPurchaseGuestsFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();
  return [
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function ActivityGuestTable(props: Props) {
  const { parentIndex } = props;
  const { editable } = useFormState();
  const { clearErrors, getValues, control, trigger, setValue } =
    useFormContext<ActivityPurchaseFormType>();

  const name =
    `activityPurchases.${parentIndex}.activityPurchaseGuests` as const;

  const columns = useGetActivityGuestTableList();

  const { update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const guests = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const guest = guests[currentRowIndex];
      const columnId = action.columnId as keyof ActivityPurchaseGuestsFormType;
      const extra = action.extra as Value;
      const type = action.type as ActionType;
      const value = action.value;
      const isGroupedPrice = getValues(
        `activityPurchases.${parentIndex}.isGroupedPrice`,
      );

      const otherVal: () => Partial<ActivityPurchaseGuestsFormType> = () => {
        if (columnId === 'guestId') {
          return { guestName: extra?.[0]?.label as string };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuest());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });

        if (isGroupedPrice && columnId === 'guestId') {
          guests.forEach((_, index) => {
            if (index === 0) return;
            setValue(
              `${name}.${index}.activityPurchaseGuestDetails`,
              guests[0].activityPurchaseGuestDetails,
            );
          });
        }
        clearErrors();
      }
    },
    [
      append,
      clearErrors,
      getValues,
      name,
      parentIndex,
      remove,
      setValue,
      trigger,
      update,
    ],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={getValues(name)}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
